import { useEffect, useRef, useState } from 'react';
import { Plane, TrainFront, Eye, Upload, CheckCircle2, AlertTriangle, HelpCircle, XCircle, FileText, Trash2 } from 'lucide-react';
import { post, postForm } from '../api/client';
import { useToast } from '../context/ToastContext';
import { Button, Modal, Badge, Select, Loader, ConfirmModal } from './UI';
import { fmtDateTime } from '../utils/format';

/**
 * Ticket tools for an event's Users tab:
 *  - TicketQuickView: the PDF/image in a modal, with the names printed on it
 *  - TicketLegs: one person's onward/return tickets, with quick view + select
 *  - BulkTicketUpload: drop a pile of PDFs, review who each one belongs to, attach
 */

export const ModeIcon = ({ mode, size = 13 }) => (mode === 'train' ? <TrainFront size={size} color="var(--gold-500)" /> : <Plane size={size} color="var(--gold-500)" />);

export function TicketQuickView({ ticket, onClose }) {
  if (!ticket) return null;
  const isPdf = /\.pdf(\?|$)/i.test(ticket.url) || !/\.(png|jpe?g|webp|gif)(\?|$)/i.test(ticket.url);
  return (
    <Modal open onClose={onClose} width={860} title={ticket.fileName || 'Ticket'}
      subtitle={[ticket.person, ticket.direction ? (ticket.direction === 'onward' ? 'Onward' : 'Return') : null].filter(Boolean).join(' · ')}>
      {ticket.passengers?.length > 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginBottom: 10 }}>
          Names on this ticket: <strong style={{ color: 'var(--text-hi)' }}>{ticket.passengers.join(', ')}</strong>
        </p>
      )}
      <div style={{ height: '70vh', background: '#fff', borderRadius: 'var(--r)', overflow: 'hidden' }}>
        {isPdf
          ? <iframe title="Ticket" src={`${ticket.url}#toolbar=0&view=FitH`} style={{ width: '100%', height: '100%', border: 0 }} />
          : <img src={ticket.url} alt="Ticket" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <a href={ticket.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: 'var(--gold-300)' }}>Open in a new tab</a>
      </div>
    </Modal>
  );
}

/** Inside the Users table: the person's journeys, each with its file(s). */
export function TicketLegs({ row, person, selected, onToggle, onView, readOnly }) {
  if (!row) return <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>—</span>;
  const legs = [['onward', 'Onward'], ['return', 'Return']].map(([k, label]) => ({ k, label, leg: row[k] }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
      {legs.map(({ k, label, leg }) => (
        <div key={k} style={{ fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: leg?.files?.length ? 'var(--text-hi)' : 'var(--text-lo)' }}>
            <span style={{ width: 46, color: 'var(--text-lo)' }}>{label}</span>
            {leg ? (
              <>
                <ModeIcon mode={leg.mode} />
                <span>{[leg.number, [leg.from?.city || leg.from?.code, leg.to?.city || leg.to?.code].filter(Boolean).join(' → ')].filter(Boolean).join(' · ') || 'Journey'}</span>
                {leg.departureAt && <span style={{ color: 'var(--text-lo)' }}>{fmtDateTime(leg.departureAt)}</span>}
                {!leg.files?.length && <Badge tone="warning">No ticket</Badge>}
              </>
            ) : <span>Not planned</span>}
          </div>
          {(leg?.files || []).map((f) => {
            const key = `${leg.flight}|${f.url}`;
            return (
              <div key={f.url} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginLeft: 52, marginTop: 3 }}>
                {!readOnly && <input type="checkbox" checked={selected.has(key)} onChange={() => onToggle(key, { flight: leg.flight, url: f.url })} style={{ accentColor: 'var(--gold-400)', marginTop: 2 }} title="Select to remove" />}
                <button type="button" onClick={() => onView({ ...f, direction: k, person: person.name })} title="Quick view"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--gold-300)', textAlign: 'left' }}>
                  <Eye size={12} /> <span style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName || 'Ticket'}</span>
                </button>
                {f.passengers?.length > 0 && (
                  <span style={{ fontSize: 11, color: f.passengers.some((n) => sameish(n, person.name)) ? 'var(--text-lo)' : 'var(--warn)', flexBasis: '100%', marginLeft: 0 }} title="Names printed on the ticket">
                    {f.passengers.join(', ')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** "Aarav Sharma · 98765 43210 · EMP1001" for the person pickers. */
const personLabel = (p) => [p.name, p.mobile ? String(p.mobile).replace(/^91(\d{10})$/, '$1') : null, p.employeeCode ? `#${p.employeeCode}` : null].filter(Boolean).join(' · ');

const norm = (s) => String(s || '').toLowerCase().replace(/^(mr|mrs|ms|miss|dr)\.?\s+/, '').replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
function sameish(a, b) {
  const x = norm(a); const y = norm(b);
  if (!x || !y) return false;
  if (x === y || x.replace(/ /g, '') === y.replace(/ /g, '')) return true;
  const tx = x.split(' '); const ty = y.split(' ');
  return tx.filter((t) => t.length > 1 && ty.includes(t)).length >= 1 && (tx[0] === ty[0] || tx[tx.length - 1] === ty[ty.length - 1]);
}

const CONF = {
  pnr: ['success', CheckCircle2, 'Matched by PNR'],
  name: ['success', CheckCircle2, 'Matched by name'],
  unsure: ['warning', HelpCircle, 'Please check'],
  none: ['danger', AlertTriangle, 'Pick a person'],
};

const MAX_BATCH = 4 * 1024 * 1024;   // Vercel's request limit; larger piles go in several requests

export function BulkTicketUpload({ open, onClose, eventId, onAttached }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [queue, setQueue] = useState([]);          // File[] waiting
  const [progress, setProgress] = useState(null);  // { done, total }
  const [files, setFiles] = useState([]);          // parsed results
  const [people, setPeople] = useState([]);
  const [rows, setRows] = useState([]);            // review rows (one per passenger × journey)
  const [notify, setNotify] = useState(true);
  const [attaching, setAttaching] = useState(false);
  const [view, setView] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => { if (open) { setQueue([]); setProgress(null); setFiles([]); setRows([]); } }, [open]);

  const addFiles = (list) => {
    const pdfs = Array.from(list || []).filter((f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf' || /^image\//.test(f.type));
    if (!pdfs.length) return toast.error('Drop PDF tickets');
    setQueue((q) => [...q, ...pdfs.filter((f) => !q.some((x) => x.name === f.name && x.size === f.size))]);
  };

  const parse = async () => {
    if (!queue.length) return;
    const batches = [];
    let cur = []; let size = 0;
    for (const f of queue) {
      if (cur.length && (size + f.size > MAX_BATCH || cur.length >= 20)) { batches.push(cur); cur = []; size = 0; }
      cur.push(f); size += f.size;
    }
    if (cur.length) batches.push(cur);

    setProgress({ done: 0, total: queue.length });
    const parsed = []; let ppl = [];
    for (const batch of batches) {
      try {
        const body = new FormData();
        body.append('event', eventId);
        batch.forEach((f) => body.append('files', f));
        const res = await postForm('/flights/tickets/bulk-parse', body);
        parsed.push(...res.data.files); ppl = res.data.people;
      } catch (err) {
        toast.error(`${batch.length} file${batch.length > 1 ? 's' : ''} failed: ${err.message}`);
      }
      setProgress((p) => ({ ...p, done: Math.min(p.total, p.done + batch.length) }));
    }
    setFiles(parsed); setPeople(ppl); setQueue([]);
    setRows(parsed.flatMap((file, fi) => (file.matches.length ? file.matches : [{ id: `${fi}-none`, passenger: '', legIndex: null, user: null, direction: 'onward', confidence: 'none', candidates: [] }]).map((m) => ({
      key: `${fi}-${m.id}`, fileIndex: fi, ...m,
      include: Boolean(m.user) && m.confidence !== 'none',
      userId: m.user?._id || '',
    }))));
    setProgress(null);
  };

  const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const attach = async () => {
    const chosen = rows.filter((r) => r.include && r.userId);
    if (!chosen.length) return toast.error('Nothing selected to attach');
    setAttaching(true);
    try {
      const items = chosen.map((r) => {
        const file = files[r.fileIndex]; const leg = r.legIndex !== null ? file.legs[r.legIndex] || {} : {};
        return { url: file.file.url, fileName: file.file.fileName, user: r.userId, direction: r.direction, mode: file.mode, passengers: file.passengers, leg, coach: leg.coachSeats?.[r.passenger] };
      });
      let attached = 0; const failed = [];
      for (let i = 0; i < items.length; i += 40) {
        const res = await post('/flights/tickets/bulk-attach', { event: eventId, items: items.slice(i, i + 40), notify });
        attached += res.data.attached; failed.push(...res.data.failed);
      }
      // Files nobody ended up using are removed from storage.
      const used = new Set(items.map((i) => i.url));
      const unused = files.map((f) => f.file.url).filter((u) => !used.has(u));
      if (unused.length) await post('/flights/tickets/discard', { urls: unused }).catch(() => {});
      toast[failed.length ? 'error' : 'success'](failed.length ? `${attached} attached, ${failed.length} failed` : `${attached} ticket${attached === 1 ? '' : 's'} attached`);
      onAttached?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAttaching(false);
    }
  };

  const discard = async () => {
    setConfirmDiscard(false);
    if (files.length) await post('/flights/tickets/discard', { urls: files.map((f) => f.file.url) }).catch(() => {});
    onClose();
  };

  const stats = rows.reduce((a, r) => { a[r.confidence] = (a[r.confidence] || 0) + 1; return a; }, {});
  const step = files.length ? 'review' : 'pick';

  return (
    <>
      <Modal open={open} onClose={() => (files.length ? setConfirmDiscard(true) : onClose())} width={step === 'review' ? 1040 : 600}
        title={step === 'review' ? 'Check who each ticket belongs to' : 'Upload tickets in bulk'}
        subtitle={step === 'review'
          ? `${files.length} file${files.length === 1 ? '' : 's'} read · ${stats.pnr || 0} matched by PNR · ${stats.name || 0} by name · ${(stats.unsure || 0) + (stats.none || 0)} to check`
          : 'Air and train tickets together — each PDF is read and matched to the people on this event'}
        footer={step === 'review' ? (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--text-mid)', marginRight: 'auto', cursor: 'pointer' }}>
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ accentColor: 'var(--gold-400)' }} /> Tell each person their ticket is ready
            </label>
            <Button variant="subtle" icon={Trash2} onClick={() => setConfirmDiscard(true)}>Discard all</Button>
            <Button onClick={attach} loading={attaching} disabled={!rows.some((r) => r.include && r.userId)}>
              Attach {rows.filter((r) => r.include && r.userId).length} ticket{rows.filter((r) => r.include && r.userId).length === 1 ? '' : 's'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="subtle" onClick={onClose}>Cancel</Button>
            <Button icon={Upload} onClick={parse} loading={Boolean(progress)} disabled={!queue.length}>
              {progress ? `Reading ${progress.done} of ${progress.total}` : `Read ${queue.length} file${queue.length === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
      >
        {step === 'pick' && (
          <>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              style={{ padding: 30, textAlign: 'center', cursor: 'pointer', border: `1.5px dashed ${queue.length ? 'var(--gold-500)' : 'var(--border)'}`, borderRadius: 'var(--r-lg)', background: queue.length ? 'rgba(239,212,138,0.05)' : 'var(--ink-900)' }}
            >
              <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              <FileText size={30} color={queue.length ? 'var(--gold-400)' : 'var(--text-lo)'} style={{ marginBottom: 10 }} />
              <p style={{ fontSize: 13.5, fontWeight: 500 }}>{queue.length ? `${queue.length} file${queue.length === 1 ? '' : 's'} ready` : 'Drop all the ticket PDFs here, or click to browse'}</p>
              <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 6 }}>You can select a whole folder's worth at once. A PDF with several passengers is attached to each of them.</p>
            </div>
            {queue.length > 0 && (
              <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 12, fontSize: 12.5, color: 'var(--text-mid)' }}>
                {queue.map((f) => (
                  <div key={f.name + f.size} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 2px' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button type="button" onClick={() => setQueue((q) => q.filter((x) => x !== f))} style={{ color: 'var(--text-lo)' }}><XCircle size={13} /></button>
                  </div>
                ))}
              </div>
            )}
            <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 14, lineHeight: 1.7 }}>
              Air tickets are matched by the PNR from the traveller sheet, then by name. Train tickets (IRCTC) are matched by name.
              Anything the system is not sure about is shown for you to pick before anything is attached.
            </p>
          </>
        )}

        {step === 'review' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: 'var(--text-lo)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {['', 'File', 'Name on ticket', 'Journey', 'Person on this event', 'Direction', 'Status'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const file = files[r.fileIndex]; const leg = r.legIndex !== null ? file.legs[r.legIndex] : null;
                  const [tone, Icon, label] = CONF[r.confidence] || CONF.none;
                  const chosenName = people.find((p) => p._id === r.userId)?.name;
                  const mismatch = r.userId && r.passenger && !sameish(r.passenger, chosenName);
                  return (
                    <tr key={r.key} style={{ borderBottom: '1px solid var(--border)', opacity: r.include ? 1 : 0.55 }}>
                      <td style={{ padding: '7px 8px' }}><input type="checkbox" checked={r.include} disabled={!r.userId} onChange={(e) => update(r.key, { include: e.target.checked })} style={{ accentColor: 'var(--gold-400)' }} /></td>
                      <td style={{ padding: '7px 8px', maxWidth: 190 }}>
                        <button type="button" onClick={() => setView({ url: file.file.url, fileName: file.file.fileName, passengers: file.passengers })} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--gold-300)', textAlign: 'left' }}>
                          <Eye size={12} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{file.file.fileName}</span>
                        </button>
                        {!file.readable && <span style={{ fontSize: 11, color: 'var(--warn)' }}>Could not read this PDF (scanned?)</span>}
                      </td>
                      <td style={{ padding: '7px 8px' }}>{r.passenger || <span style={{ color: 'var(--text-lo)' }}>—</span>}</td>
                      <td style={{ padding: '7px 8px', whiteSpace: 'nowrap' }}>
                        {leg ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ModeIcon mode={leg.mode} />
                            <span>{leg.number} {[leg.from?.code || leg.from?.city, leg.to?.code || leg.to?.city].filter(Boolean).join(' → ')}</span>
                            {leg.departureAt && <span style={{ color: 'var(--text-lo)' }}>{fmtDateTime(leg.departureAt)}</span>}
                          </span>
                        ) : <span style={{ color: 'var(--text-lo)' }}>Not found in file</span>}
                      </td>
                      <td style={{ padding: '7px 8px', minWidth: 200 }}>
                        <Select value={r.userId} onChange={(e) => update(r.key, { userId: e.target.value, include: Boolean(e.target.value) })} style={{ fontSize: 12.5, borderColor: r.userId ? undefined : 'var(--warn)' }}>
                          <option value="">— pick a person —</option>
                          {r.candidates?.length > 0 && <optgroup label="Likely">{r.candidates.map((c) => <option key={c._id} value={c._id}>{personLabel(c)} ({c.score}%)</option>)}</optgroup>}
                          <optgroup label="Everyone on the event">{people.filter((p) => !r.candidates?.some((c) => c._id === p._id)).map((p) => <option key={p._id} value={p._id}>{personLabel(p)}</option>)}</optgroup>
                        </Select>
                        {r.userId && (() => { const p = people.find((x) => x._id === r.userId); return p ? <span style={{ fontSize: 11, color: 'var(--text-lo)', display: 'block', marginTop: 3 }}>{[p.mobile ? `📱 ${String(p.mobile).replace(/^91(\d{10})$/, '$1')}` : null, p.employeeCode ? `Emp ID ${p.employeeCode}` : null].filter(Boolean).join(' · ') || 'No mobile or employee id on record'}</span> : null; })()}
                        {r.hasTicketAlready && r.include && <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>Already has a ticket for this journey — this one is added too</span>}
                      </td>
                      <td style={{ padding: '7px 8px' }}>
                        <Select value={r.direction} onChange={(e) => update(r.key, { direction: e.target.value })} style={{ fontSize: 12.5, width: 110 }}>
                          <option value="onward">Onward</option><option value="return">Return</option>
                        </Select>
                      </td>
                      <td style={{ padding: '7px 8px' }}>
                        <Badge tone={mismatch ? 'warning' : tone}><Icon size={10} /> {mismatch ? 'Name differs' : label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {progress && <Loader label="Reading tickets" />}
          </div>
        )}
      </Modal>

      {view && <TicketQuickView ticket={view} onClose={() => setView(null)} />}
      <ConfirmModal open={confirmDiscard} onClose={() => setConfirmDiscard(false)} onConfirm={discard} danger title="Discard these uploads?" confirmLabel="Discard"
        message="The files you just uploaded will be deleted and nothing will be attached." />
    </>
  );
}
